import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Plug,
  ShieldCheck,
  SlidersHorizontal,
  Unplug,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ChatPageAccessDto, EmailSettingsUpdateDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { CopyField } from '@/Domain/components/Integration/components/CopyField';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { PostHogEventEnum, type EmailDomainVerifyOutcome } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useIntegrationConnectFunnel } from '@/Domain/hooks/Integration/useIntegrationConnectFunnel.hook';

const EMAIL_NS = 'integration:email';
const EMAIL_INTEGRATION_NAME = 'email' as const;
const EMAIL_QUERY_KEY = [QueryKey.integration.get, EMAIL_INTEGRATION_NAME] as const;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Deep-link to the domain/DNS section of the Email channel docs.
const DOMAIN_DOCS_URL =
  'https://docs.quickchat.ai/channels/email#send-replies-from-your-own-domain';

// Mirrors EmailIntegrationParams in
// backend/appquickchat/appquickchat/api/integrations/integration_params_schemas.py.
interface DnsRecord {
  key: string;
  type: string;
  host: string;
  data: string;
  valid: boolean;
  reason?: string;
}

interface EmailIntegrationParams {
  email_bot_active: boolean;
  inbound_address: string | null;
  reply_from_address: string | null;
  display_name: string | null;
  signature: string | null;
  domain_authenticated: boolean | null;
  is_public_domain: boolean | null;
  dns_records: DnsRecord[] | null;
  forwarding_confirmation: string | null;
  forwarding_active: boolean | null;
  rating_footer_active: boolean | null;
}

interface EmailIntegrationResponse {
  name: typeof EMAIL_INTEGRATION_NAME;
  params: EmailIntegrationParams;
}

interface VerifyDomainResponse {
  status: string;
  domain_authenticated: boolean;
  message?: string;
}

interface EmailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EmailSheet: React.FC<EmailSheetProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation(['integration', 'integrations']);
  const queryClient = useQueryClient();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const [replyAddress, setReplyAddress] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [signature, setSignature] = useState('');

  const integrationQuery = useQuery<EmailIntegrationResponse>({
    queryKey: EMAIL_QUERY_KEY,
    enabled: open,
    // Poll while open until forwarding is observed, so the "forwarding active"
    // indicator flips on its own once the first forwarded email lands (the shared
    // query key otherwise inherits staleTime: Infinity and never refetches).
    refetchInterval: (query) =>
      open && !query.state.data?.params?.forwarding_active ? 15000 : false,
  });
  const chatPageAccessQuery = useQuery<ChatPageAccessDto>({
    queryKey: [QueryKey.chatPageAccess.get],
    enabled: open,
  });
  const data = integrationQuery.data?.params;
  const isConnected = Boolean(data?.inbound_address);
  const hasSupportInbox = Boolean(data?.reply_from_address?.trim());

  const { capture } = usePostHogAnalytics();
  const { trackConnectStarted, trackConnectFailed } = useIntegrationConnectFunnel(
    EMAIL_INTEGRATION_NAME,
    isConnected,
  );

  // `null` until the first loaded response, so reopening an already-forwarding integration seeds instead of reporting.
  const forwardingSeen = useRef<boolean | null>(null);
  const forwardingActive = Boolean(data?.forwarding_active);
  useEffect(() => {
    if (!open) {
      forwardingSeen.current = null;
      return;
    }
    if (!data) return;
    const previous = forwardingSeen.current;
    forwardingSeen.current = forwardingActive;
    if (previous !== false || !forwardingActive) return;
    capture(PostHogEventEnum.EmailIntegrationForwardingDetected, {
      has_support_inbox: hasSupportInbox,
      bot_active: Boolean(data.email_bot_active),
    });
  }, [open, data, forwardingActive, hasSupportInbox, capture]);

  const captureAddressCopied = useCallback(
    () =>
      capture(PostHogEventEnum.EmailIntegrationAddressCopied, {
        forwarding_active: forwardingActive,
      }),
    [capture, forwardingActive],
  );

  const captureVerifyOutcome = useCallback(
    (outcome: EmailDomainVerifyOutcome) =>
      capture(PostHogEventEnum.EmailIntegrationDomainVerifyAttempted, { outcome }),
    [capture],
  );

  // Seed editable fields once per connected integration; re-seeding on every refetch would clobber an in-progress edit in another field.
  const seededAddress = useRef<string | null>(null);
  useEffect(() => {
    const address = data?.inbound_address ?? null;
    if (!data || seededAddress.current === address) return;
    seededAddress.current = address;
    setReplyAddress(data.reply_from_address ?? '');
    setDisplayName(data.display_name ?? '');
    setSignature(data.signature ?? '');
  }, [data]);

  const replyAddressValid = EMAIL_RE.test(replyAddress.trim());
  // Enable a Save only when there is a valid, unsaved change, so the buttons
  // behave the same instead of one being always-clickable (empty saves) and the
  // other permanently disabled.
  const replyIdentityDirty =
    replyAddress !== (data?.reply_from_address ?? '') || displayName !== (data?.display_name ?? '');
  const signatureDirty = signature !== (data?.signature ?? '');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: EMAIL_QUERY_KEY });

  const connectMutation = useMutation<EmailIntegrationResponse>({
    mutationKey: [QueryKey.integration.emailConnect],
    // Open the required setup immediately after generating the forwarding address.
    // The channel remains off until the support inbox is saved and the user enables it.
    onSuccess: (response) => {
      if (response?.params) queryClient.setQueryData(EMAIL_QUERY_KEY, response);
      invalidate();
      setConfigDialogOpen(true);
    },
    onError: () => {
      trackConnectFailed('connect_failed');
      toast.error(t(`${EMAIL_NS}.connect_failed`));
    },
  });

  const replyAddressMutation = useMutation<
    unknown,
    Error,
    { reply_from_address: string; display_name: string }
  >({
    mutationKey: [QueryKey.integration.emailReplyAddress],
    onSuccess: (_result, variables) => {
      invalidate();
      capture(PostHogEventEnum.EmailIntegrationSupportInboxSaved, {
        is_first_save: !hasSupportInbox,
        has_display_name: Boolean(variables.display_name.trim()),
      });
      toast.success(t(`${EMAIL_NS}.saved`));
    },
    onError: () => {
      capture(PostHogEventEnum.EmailIntegrationSupportInboxSaveFailed, {
        reason: 'save_failed',
      });
      toast.error(t(`${EMAIL_NS}.save_failed`));
    },
  });

  const verifyMutation = useMutation<VerifyDomainResponse>({
    mutationKey: [QueryKey.integration.emailVerifyDomain],
    onSuccess: (result) => {
      invalidate();
      captureVerifyOutcome(result.domain_authenticated ? 'authenticated' : 'pending');
      if (result.domain_authenticated) {
        toast.success(t(`${EMAIL_NS}.verify_succeeded`));
      } else {
        toast.error(result.message || t(`${EMAIL_NS}.verify_pending`));
      }
    },
    onError: () => {
      captureVerifyOutcome('failed');
      toast.error(t(`${EMAIL_NS}.verify_failed`));
    },
  });

  const settingsMutation = useMutation<unknown, Error, EmailSettingsUpdateDto>({
    mutationKey: [QueryKey.integration.emailSettings],
    onSuccess: () => {
      invalidate();
      toast.success(t(`${EMAIL_NS}.saved`));
    },
    onError: () => toast.error(t(`${EMAIL_NS}.save_failed`)),
  });

  const toggleActiveMutation = useMutation<
    unknown,
    Error,
    { name: typeof EMAIL_INTEGRATION_NAME; params: { email_bot_active: boolean } }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (_d, variables) => {
      invalidate();
      capture(PostHogEventEnum.EmailIntegrationActiveToggled, {
        enabled: variables.params.email_bot_active,
        forwarding_active: forwardingActive,
      });
      const name = t(`${EMAIL_NS}.title`);
      toast.success(
        variables.params.email_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: (_e, variables) => {
      capture(PostHogEventEnum.EmailIntegrationActiveToggleFailed, {
        enabled: variables.params.email_bot_active,
        reason: 'save_failed',
      });
      toast.error(t(`${EMAIL_NS}.save_failed`));
    },
  });

  const removeMutation = useMutation({
    mutationKey: [QueryKey.integration.emailRemove],
    onSuccess: () => {
      invalidate();
      capture(PostHogEventEnum.EmailIntegrationRemoved, {
        was_active: Boolean(data?.email_bot_active),
        forwarding_active: forwardingActive,
      });
      setConfirmRemoveOpen(false);
      setConfigDialogOpen(false);
    },
    onError: () => toast.error(t(`${EMAIL_NS}.remove_failed`)),
  });

  const prerequisitesRaw = t(`${EMAIL_NS}.prerequisites`, { returnObjects: true });
  const prerequisites = Array.isArray(prerequisitesRaw) ? (prerequisitesRaw as string[]) : [];
  const forwardingStepsRaw = t(`${EMAIL_NS}.forwarding_steps`, { returnObjects: true });
  const forwardingSteps = Array.isArray(forwardingStepsRaw) ? (forwardingStepsRaw as string[]) : [];
  const isChatPageReachable = (chatPageAccessQuery.data?.chat_page_access ?? 'public') === 'public';
  const ratingFooterActive = data?.rating_footer_active ?? false;
  const ratingFooterSaving =
    settingsMutation.isPending && settingsMutation.variables?.rating_footer_active !== undefined;
  const signatureSaving =
    settingsMutation.isPending &&
    settingsMutation.variables?.rating_footer_active === undefined &&
    settingsMutation.variables?.signature !== undefined;
  const dnsRecords = data?.dns_records ?? [];
  const isPublicDomain = Boolean(data?.is_public_domain);
  const isDomainAuthenticated = Boolean(data?.domain_authenticated);
  const supportInboxSummary =
    data?.reply_from_address || t(`${EMAIL_NS}.support_inbox_not_configured`);
  const optionalBadge = (
    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {t(`${EMAIL_NS}.optional`)}
    </span>
  );
  const domainDocsLink = (
    <a
      href={DOMAIN_DOCS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="flex w-fit items-center gap-1 text-xs font-medium text-foreground hover:text-muted-foreground"
    >
      {t(`${EMAIL_NS}.domain_docs_link`)}
      <ArrowUpRight className="size-3.5" />
    </a>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0"
        tabIndex={undefined}
        data-testid="email-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <Mail className="size-8 shrink-0" />
            <div className="flex items-center gap-2">
              <SheetTitle data-testid="email-sheet-title">{t(`${EMAIL_NS}.title`)}</SheetTitle>
              <IntegrationStatusBadge connected={isConnected} />
              <IntegrationEnabledBadge enabled={Boolean(data?.email_bot_active)} />
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="email-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
          {integrationQuery.isLoading ? (
            <div className="flex items-center justify-center py-12" data-testid="email-loading">
              <Loader2 className="size-6 animate-spin text-foreground-subtle" />
            </div>
          ) : (
            <>
              <IntegrationIntroduction
                description={t(`${EMAIL_NS}.overview`)}
                prerequisites={prerequisites}
                docsHref="https://docs.quickchat.ai/channels/email"
              />

              <IntegrationSection
                title={t('integrations:section.connect')}
                data-testid="email-connect-section"
              >
                {!isConnected ? (
                  <Button
                    type="button"
                    variant="dark"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      trackConnectStarted();
                      connectMutation.mutate(undefined);
                    }}
                    disabled={connectMutation.isPending}
                    data-testid="email-connect-button"
                  >
                    {connectMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Plug className="size-4" />
                    )}
                    {t(`${EMAIL_NS}.connect`)}
                  </Button>
                ) : (
                  <IntegrationConnectionCard
                    data-testid="email-connection-card"
                    details={[
                      {
                        label: t(`${EMAIL_NS}.forwarding_label`),
                        value: data?.forwarding_active ? (
                          <span
                            className="flex items-center gap-1 text-success"
                            data-testid="email-card-forwarding"
                          >
                            <CheckCircle2 className="size-3.5" />
                            {t(`${EMAIL_NS}.forwarding_status_active`)}
                          </span>
                        ) : (
                          <span
                            className="flex items-center gap-1 text-muted-foreground"
                            data-testid="email-card-forwarding"
                          >
                            <Clock className="size-3.5" />
                            {t(`${EMAIL_NS}.forwarding_status_pending`)}
                          </span>
                        ),
                      },
                      {
                        label: t(`${EMAIL_NS}.reply_address_label`),
                        value: supportInboxSummary,
                        'data-testid': 'email-card-support-inbox',
                      },
                    ]}
                    actions={
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConfigDialogOpen(true)}
                          data-testid="email-settings-open-button"
                        >
                          <SlidersHorizontal className="size-4" />
                          {t(`${EMAIL_NS}.configure`)}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConfirmRemoveOpen(true)}
                          data-testid="email-remove-button"
                        >
                          <Unplug className="size-4" />
                          {t('integrations:disconnect')}
                        </Button>
                      </>
                    }
                  />
                )}
              </IntegrationSection>

              {isConnected ? (
                <IntegrationSection
                  title={t('integrations:section.settings')}
                  data-testid="email-enable-section"
                >
                  <IntegrationToggleField
                    id="email-active-toggle"
                    label={t('integrations:enable', { name: t(`${EMAIL_NS}.title`) })}
                    description={
                      hasSupportInbox
                        ? undefined
                        : t(`${EMAIL_NS}.activation_requires_support_inbox`)
                    }
                    checked={Boolean(data?.email_bot_active)}
                    onCheckedChange={(checked) =>
                      toggleActiveMutation.mutate({
                        name: EMAIL_INTEGRATION_NAME,
                        params: { email_bot_active: checked },
                      })
                    }
                    disabled={
                      toggleActiveMutation.isPending ||
                      (!hasSupportInbox && !data?.email_bot_active)
                    }
                    data-testid="email-active-toggle"
                  />
                </IntegrationSection>
              ) : null}
            </>
          )}
        </div>

        <DialogPrimitive.Root open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
          <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <DialogPrimitive.Content
            className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
            data-testid="email-settings-dialog"
            aria-describedby={undefined}
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              setConfigDialogOpen(false);
            }}
            // Clicking the backdrop around the card (the content padding, not a
            // child) dismisses it, so X is not the only way out.
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfigDialogOpen(false);
            }}
          >
            <div className="flex max-h-full w-full max-w-lg flex-col overflow-y-auto rounded-lg bg-card p-6 shadow-lg">
              <div className="mb-6 flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                  <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
                    {t(`${EMAIL_NS}.configure`)}
                  </DialogPrimitive.Title>
                  <p className="text-sm text-foreground-subtle text-pretty">
                    {t(`${EMAIL_NS}.configure_subtitle`)}
                  </p>
                </div>
                <IconButton
                  type="button"
                  variant="secondary"
                  onClick={() => setConfigDialogOpen(false)}
                  className="shrink-0"
                  data-testid="email-settings-dialog-close"
                >
                  <CloseIcon />
                </IconButton>
              </div>

              <div className="space-y-6">
                <div className="space-y-4" data-testid="email-forwarding-section">
                  <CopyField
                    label={t(`${EMAIL_NS}.address_label`)}
                    value={data?.inbound_address ?? ''}
                    testId="email-address"
                    copyTestId="email-address-copy"
                    tooltip={t(`${EMAIL_NS}.address_tooltip`)}
                    onCopy={captureAddressCopied}
                  />

                  {data?.forwarding_active ? (
                    // Once a forwarded email has arrived the setup steps and the
                    // "waiting" prompt are done: collapse to a confirmation.
                    <p
                      className="flex items-center gap-1.5 text-sm text-success"
                      data-testid="email-forwarding-active"
                    >
                      <CheckCircle2 className="size-4" />
                      {t(`${EMAIL_NS}.forwarding_active`)}
                    </p>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        <p className="text-sm font-medium text-foreground">
                          {t(`${EMAIL_NS}.forwarding_title`)}
                        </p>
                        <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground marker:text-foreground-subtle">
                          {forwardingSteps.map((step, index) => (
                            <li key={index}>{step}</li>
                          ))}
                        </ol>
                      </div>
                      <p
                        className="flex items-center gap-1.5 text-sm text-muted-foreground"
                        data-testid="email-forwarding-pending"
                      >
                        <Clock className="size-4" />
                        {t(`${EMAIL_NS}.forwarding_pending`)}
                      </p>
                    </>
                  )}

                  {data?.forwarding_confirmation ? (
                    <div
                      className="rounded-lg border border-info-muted bg-info-muted/30 p-3 text-sm text-foreground"
                      data-testid="email-forwarding-confirmation"
                    >
                      <p className="font-medium">{t(`${EMAIL_NS}.confirmation_title`)}</p>
                      <p className="text-muted-foreground break-words">
                        {data.forwarding_confirmation}
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="space-y-4" data-testid="email-reply-identity-section">
                  <div className="space-y-1.5">
                    <p className="text-sm font-semibold text-foreground">
                      {t(`${EMAIL_NS}.reply_identity_title`)}
                    </p>
                    <p className="text-sm text-muted-foreground text-pretty">
                      {t(`${EMAIL_NS}.reply_identity_help`)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="email-display-name" className="text-sm font-medium">
                        {t(`${EMAIL_NS}.display_name_label`)}
                      </Label>
                      {optionalBadge}
                    </div>
                    <Input
                      id="email-display-name"
                      size="sm"
                      value={displayName}
                      placeholder={t(`${EMAIL_NS}.display_name_placeholder`)}
                      onChange={(e) => setDisplayName(e.target.value)}
                      data-testid="email-display-name-input"
                    />
                    <p className="text-xs text-foreground-subtle text-pretty">
                      {t(`${EMAIL_NS}.display_name_help`)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-reply-address" className="text-sm font-medium">
                      {t(`${EMAIL_NS}.reply_address_label`)}
                    </Label>
                    <Input
                      id="email-reply-address"
                      type="email"
                      size="sm"
                      value={replyAddress}
                      placeholder={t(`${EMAIL_NS}.reply_address_placeholder`)}
                      onChange={(e) => setReplyAddress(e.target.value)}
                      data-testid="email-reply-address-input"
                    />
                    <p className="text-xs text-foreground-subtle text-pretty">
                      {t(`${EMAIL_NS}.reply_address_help`)}
                    </p>
                    {replyAddress && !replyAddressValid ? (
                      <p
                        className="text-xs text-destructive"
                        data-testid="email-reply-address-error"
                      >
                        {t(`${EMAIL_NS}.reply_address_invalid`)}
                      </p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      replyAddressMutation.mutate({
                        reply_from_address: replyAddress,
                        display_name: displayName,
                      })
                    }
                    disabled={
                      !replyAddressValid || !replyIdentityDirty || replyAddressMutation.isPending
                    }
                    data-testid="email-reply-address-save"
                  >
                    {replyAddressMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : null}
                    {t(`${EMAIL_NS}.reply_address_save`)}
                  </Button>
                </div>

                {data?.reply_from_address ? (
                  <div className="space-y-4" data-testid="email-branded-section">
                    <p className="text-sm font-semibold text-foreground">
                      {t(`${EMAIL_NS}.branded_title`)}
                    </p>
                    {isPublicDomain ? (
                      <p
                        className="text-sm text-muted-foreground text-pretty"
                        data-testid="email-public-domain-note"
                      >
                        {t(`${EMAIL_NS}.public_domain_note`)}
                      </p>
                    ) : isDomainAuthenticated ? (
                      <p
                        className="flex items-center gap-1.5 text-sm text-success"
                        data-testid="email-domain-verified"
                      >
                        <ShieldCheck className="size-4" />
                        {t(`${EMAIL_NS}.domain_verified`)}
                      </p>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground text-pretty">
                          {t(`${EMAIL_NS}.dns_intro`)}
                        </p>
                        {domainDocsLink}
                        {dnsRecords.map((record) => (
                          <div
                            key={record.key}
                            className="space-y-2 rounded-lg border border-border p-3"
                            data-testid={`email-dns-${record.key}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-foreground-subtle uppercase">
                                {record.type}
                              </span>
                              {record.valid ? (
                                <span className="flex items-center gap-1 text-xs text-success">
                                  <CheckCircle2 className="size-3.5" />
                                  {t(`${EMAIL_NS}.dns_verified`)}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Clock className="size-3.5" />
                                  {t(`${EMAIL_NS}.dns_pending`)}
                                </span>
                              )}
                            </div>
                            <CopyField
                              label={t(`${EMAIL_NS}.dns_host`)}
                              value={record.host}
                              testId={`email-dns-host-${record.key}`}
                              copyTestId={`email-dns-host-copy-${record.key}`}
                            />
                            <CopyField
                              label={t(`${EMAIL_NS}.dns_value`)}
                              value={record.data}
                              testId={`email-dns-value-${record.key}`}
                              copyTestId={`email-dns-value-copy-${record.key}`}
                            />
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => verifyMutation.mutate()}
                          disabled={verifyMutation.isPending}
                          data-testid="email-verify-button"
                        >
                          {verifyMutation.isPending ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="size-4" />
                          )}
                          {t(`${EMAIL_NS}.verify`)}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="space-y-2" data-testid="email-rating-footer-section">
                  <IntegrationToggleField
                    id="email-rating-footer-toggle"
                    label={t(`${EMAIL_NS}.rating_footer.label`)}
                    description={t(`${EMAIL_NS}.rating_footer.description`)}
                    tooltip={t(`${EMAIL_NS}.rating_footer.tooltip`)}
                    checked={ratingFooterActive}
                    onCheckedChange={(checked) =>
                      settingsMutation.mutate({
                        rating_footer_active: checked,
                        signature: data?.signature ?? '',
                      })
                    }
                    disabled={settingsMutation.isPending}
                    loading={ratingFooterSaving}
                    data-testid="email-rating-footer-switch"
                  />
                  {ratingFooterActive && !isChatPageReachable ? (
                    <p
                      className="flex items-start gap-1.5 text-xs text-muted-foreground"
                      data-testid="email-rating-footer-chat-page-warning"
                    >
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
                      {t(`${EMAIL_NS}.rating_footer.chat_page_warning`)}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-2" data-testid="email-settings-section">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="email-signature" className="text-sm font-medium">
                      {t(`${EMAIL_NS}.signature_label`)}
                    </Label>
                    {optionalBadge}
                  </div>
                  <AutosizeTextarea
                    id="email-signature"
                    className="px-3"
                    minHeight={72}
                    value={signature}
                    placeholder={t(`${EMAIL_NS}.signature_placeholder`)}
                    onChange={(e) => setSignature(e.target.value)}
                    data-testid="email-signature-input"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => settingsMutation.mutate({ signature })}
                    disabled={!signatureDirty || settingsMutation.isPending}
                    data-testid="email-signature-save"
                  >
                    {signatureSaving ? <Loader2 className="size-4 animate-spin" /> : null}
                    {t(`${EMAIL_NS}.signature_save`)}
                  </Button>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <Button
                  type="button"
                  variant="dark"
                  size="md"
                  onClick={() => setConfigDialogOpen(false)}
                  data-testid="email-settings-done-button"
                >
                  {t('integrations:done')}
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Root>

        <AlertDialogPrimitive.Root open={confirmRemoveOpen} onOpenChange={setConfirmRemoveOpen}>
          <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
          <AlertDialogPrimitive.Content className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none">
            <div
              className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg"
              data-testid="email-remove-dialog"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-6 text-destructive" />
                <h2 className="text-lg font-semibold text-foreground">
                  {t(`${EMAIL_NS}.remove_confirm.title`)}
                </h2>
              </div>
              <p className="text-sm text-muted-foreground">
                {t(`${EMAIL_NS}.remove_confirm.description`)}
              </p>
              <div className="flex justify-end gap-2">
                <AlertDialogPrimitive.Cancel asChild>
                  <Button variant="secondary" size="md" data-testid="email-remove-cancel-button">
                    {t(`${EMAIL_NS}.remove_confirm.cancel`)}
                  </Button>
                </AlertDialogPrimitive.Cancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => removeMutation.mutate(undefined)}
                  disabled={removeMutation.isPending}
                  data-testid="email-remove-confirm-button"
                >
                  {removeMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <XCircle className="size-4" />
                  )}
                  {t(`${EMAIL_NS}.remove_confirm.confirm`)}
                </Button>
              </div>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
